import { onUnmounted, getCurrentInstance } from "vue";
import { EventManager } from "@events";

/**
 * Vue 3 composable that provides scoped event/action emitters for a given rtcId.
 * All listeners registered via the returned `.on()` are automatically removed
 * when the component unmounts — preventing the duplicate-handler bug that occurs
 * when a component is mounted and unmounted repeatedly (e.g. menu expand/collapse).
 *
 * Usage:
 * ```js
 * const { eventEmitter, actionEmitter } = useEventEmitters(props.rtcId);
 *
 * onMounted(() => {
 *   eventEmitter.on(Events.SignalingMessageReceived, handler);
 *   actionEmitter.on("my-action", otherHandler);
 * });
 * // No need for onUnmounted cleanup — it's automatic.
 * ```
 *
 * The returned objects proxy the real EventEmitter2 instances but track every
 * `.on()` call so it can be undone on unmount. `.emit()` and other methods
 * pass through directly.
 */
export function useEventEmitters(rtcId) {
  const realEvent = EventManager.getEventEmitter(rtcId);
  const realAction = EventManager.getActionEmitter(rtcId);

  const trackedListeners = [];

  function createScopedEmitter(realEmitter) {
    return {
      on(event, handler) {
        realEmitter.on(event, handler);
        trackedListeners.push({ emitter: realEmitter, event, handler });
      },
      off(event, handler) {
        realEmitter.off(event, handler);
        // Also remove from tracked list so we don't double-off on unmount
        const idx = trackedListeners.findIndex(
          (t) => t.emitter === realEmitter && t.event === event && t.handler === handler
        );
        if (idx !== -1) trackedListeners.splice(idx, 1);
      },
      emit(...args) {
        return realEmitter.emit(...args);
      },
      /** Escape hatch: access the underlying EventEmitter2 instance directly. */
      get raw() {
        return realEmitter;
      },
    };
  }

  const eventEmitter = createScopedEmitter(realEvent);
  const actionEmitter = createScopedEmitter(realAction);

  // Auto-cleanup only if called inside a component setup context
  if (getCurrentInstance()) {
    onUnmounted(() => {
      for (const { emitter, event, handler } of trackedListeners) {
        emitter.off(event, handler);
      }
      trackedListeners.length = 0;
    });
  }

  return { eventEmitter, actionEmitter };
}
